import React from 'react';
import Panel from '../../components/ui/Panel';
import CodeBlock from '../../components/ui/CodeBlock';

const TypographyPage: React.FC = () => {
  const tailwindConfigCode = `// tailwind.config.js
module.exports = {
  theme: {
    extend: {
      fontFamily: {
        'runescape': ['RuneScape'],
        'runescape-small': ['RuneScape Small'],
        'runescape-bold': ['RuneScape Bold'],
      },
    },
  },
}`;

  const cssCode = `/* Font faces */
@font-face {
  font-family: 'RuneScape';
  src: url('./assets/fonts/Runescape Font.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'RuneScape Small';
  src: url('./assets/fonts/Runescape Small Font.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'RuneScape Bold';
  src: url('./assets/fonts/Runescape Bold.ttf') format('truetype');
  font-weight: bold;
  font-style: normal;
  font-display: swap;
}`;

  return (
    <div>
      <h1 id="overview" className="text-3xl text-osrs-gold font-bold uppercase mb-4">Typography</h1>
      <p className="text-osrs-yellow mb-8">
        Typography in the OSRS Design System is based on the authentic RuneScape fonts from the game.
        The pixel font style is a key element in maintaining the nostalgic and distinctive OSRS look.
      </p>
      
      <section id="fonts" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Font Families</h2>
        <p className="text-osrs-yellow mb-6">
          Three primary font families are used throughout the OSRS interface, each serving specific purposes.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Panel variant="default">
            <h3 className="text-xl text-osrs-gold font-bold uppercase mb-3">RuneScape</h3>
            <p className="text-osrs-yellow mb-2 font-runescape text-xl">The quick brown fox jumps over the lazy dog</p>
            <p className="text-osrs-yellow mb-2 font-runescape text-xl">ABCDEFGHIJKLMNOPQRSTUVWXYZ</p>
            <p className="text-osrs-yellow mb-4 font-runescape text-xl">0123456789</p>
            <p className="text-osrs-chat text-sm">Main body text and general interface elements</p>
          </Panel>
          
          <Panel variant="default">
            <h3 className="text-xl text-osrs-gold font-bold uppercase mb-3">RuneScape Small</h3>
            <p className="text-osrs-yellow mb-2 font-runescape-small text-lg">The quick brown fox jumps over the lazy dog</p>
            <p className="text-osrs-yellow mb-2 font-runescape-small text-lg">ABCDEFGHIJKLMNOPQRSTUVWXYZ</p>
            <p className="text-osrs-yellow mb-4 font-runescape-small text-lg">0123456789</p>
            <p className="text-osrs-chat text-sm">Small text, captions, and dense information</p>
          </Panel>
          
          <Panel variant="default">
            <h3 className="text-xl text-osrs-gold font-bold uppercase mb-3">RuneScape Bold</h3>
            <p className="text-osrs-yellow mb-2 font-runescape-bold text-xl">The quick brown fox jumps over the lazy dog</p>
            <p className="text-osrs-yellow mb-2 font-runescape-bold text-xl">ABCDEFGHIJKLMNOPQRSTUVWXYZ</p>
            <p className="text-osrs-yellow mb-4 font-runescape-bold text-xl">0123456789</p>
            <p className="text-osrs-chat text-sm">Headings, emphasis, and important information</p>
          </Panel>
        </div>
      </section>
      
      <section id="scale" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Type Scale</h2>
        <p className="text-osrs-yellow mb-6">
          The OSRS Design System uses a consistent type scale to maintain visual hierarchy and readability.
        </p>
        
        <Panel className="mb-8">
          <h1 className="text-3xl text-osrs-gold font-runescape-bold mb-4">Heading 1 (text-3xl)</h1>
          <h2 className="text-2xl text-osrs-gold font-runescape-bold mb-4">Heading 2 (text-2xl)</h2>
          <h3 className="text-xl text-osrs-gold font-runescape-bold mb-4">Heading 3 (text-xl)</h3>
          <h4 className="text-lg text-osrs-gold font-runescape-bold mb-4">Heading 4 (text-lg)</h4>
          <p className="text-base text-osrs-yellow font-runescape mb-4">Body Text (text-base)</p>
          <p className="text-sm text-osrs-yellow font-runescape-small mb-4">Small Text (text-sm)</p>
          <p className="text-xs text-osrs-yellow font-runescape-small">Extra Small Text (text-xs)</p>
        </Panel>
      </section>
      
      <section id="styles" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Text Styles</h2>
        <p className="text-osrs-yellow mb-6">
          OSRS text styles include various formats and colors to indicate different types of information.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Panel>
            <h3 className="text-xl text-osrs-gold font-bold uppercase mb-3">Headers</h3>
            <div className="space-y-4">
              <p className="text-2xl text-osrs-gold font-runescape-bold uppercase">Page Title</p>
              <p className="text-xl text-osrs-gold font-runescape-bold uppercase">Section Header</p>
              <p className="text-lg text-osrs-gold font-runescape-bold uppercase">Subsection Header</p>
            </div>
          </Panel>
          
          <Panel>
            <h3 className="text-xl text-osrs-gold font-bold uppercase mb-3">Game-specific Text</h3>
            <div className="space-y-4">
              <p className="text-osrs-yellow font-runescape">Normal dialogue text</p>
              <p className="text-osrs-blue font-runescape">Magic/special item text</p>
              <p className="text-osrs-green font-runescape">Positive value/status</p>
              <p className="text-osrs-red font-runescape">Negative value/warning</p>
              <p className="text-osrs-chat font-runescape">Chat message</p>
            </div>
          </Panel>
        </div>
      </section>
      
      <section id="implementation" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Implementation</h2>
        
        <Panel title="Font Imports" className="mb-6">
          <p className="text-osrs-yellow mb-4">
            Include the OSRS fonts in your project using @font-face:
          </p>
          <CodeBlock code={cssCode} language="css" />
        </Panel>
        
        <Panel title="Tailwind Config" className="mb-6">
          <p className="text-osrs-yellow mb-4">
            Add the font families to your Tailwind CSS configuration:
          </p>
          <CodeBlock code={tailwindConfigCode} language="jsx" />
        </Panel>
      </section>
      
      <section id="usage" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Usage Guidelines</h2>
        
        <Panel id="accessibility" className="mb-6">
          <h3 className="text-xl text-osrs-gold font-bold mb-3">Accessibility</h3>
          <p className="text-osrs-yellow mb-2">
            While maintaining the OSRS aesthetic, consider these accessibility best practices:
          </p>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use a minimum font size of 16px (text-base) for body text</li>
            <li>Provide sufficient contrast between text and background</li>
            <li>For small text, prefer RuneScape Small font for better readability</li>
            <li>Consider adding text shadows when placing text over busy backgrounds</li>
          </ul>
        </Panel>
        
        <Panel id="best-practices" className="mb-6">
          <h3 className="text-xl text-osrs-gold font-bold mb-3">Best Practices</h3>
          <p className="text-osrs-yellow mb-2">
            Follow these principles when using OSRS typography:
          </p>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use uppercase text for headings to match OSRS style</li>
            <li>Maintain consistent text colors based on the content type</li>
            <li>Limit the use of text styles to avoid visual confusion</li>
            <li>For titles and headers, use the RuneScape Bold font</li>
            <li>For dense information areas, use RuneScape Small font</li>
          </ul>
        </Panel>
      </section>
    </div>
  );
};

export default TypographyPage; 